import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPlants } from '../api';
import PlantCard from '../components/PlantCard';
import SearchBar from '../components/SearchBar';
import WeatherWidget from '../components/WeatherWidget';
import { MonthPanel, GuideNotes } from '../components/GardenGuide';
import PhotoGallery from '../components/PhotoGallery';
import { CATEGORIES } from '../data/categories';
import guides from '../data/guides';

// Hero photograph: the garden's own dahlia and marigold bed, cropped from
// gallery/flowers/flower-010.jpg and re-encoded at two widths. Served from
// our own origin, so no third-party image host and no attribution needed.
const HERO_IMAGE = '/hero-garden.jpg';
const HERO_IMAGE_SMALL = '/hero-garden-900.jpg';

const FILTERS = [{ value: '', label: 'All plants' }, ...CATEGORIES];

export default function Home() {
  const [plants, setPlants] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  // Total across the whole collection, captured from the first
  // (unfiltered) load so the stats strip doesn't change as filters are
  // applied.
  const [total, setTotal] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (search) params.search = search;
    getPlants(params)
      .then(data => {
        const list = data.plants || [];
        setPlants(list);
        if (!category && !search) setTotal(list.length);
      })
      // A failed request previously surfaced as an unhandled rejection and
      // left the grid on its empty state with no explanation.
      .catch(() => setPlants([]))
      .finally(() => setLoading(false));
  }, [search, category]);

  const filtered = Boolean(search || category);

  return (
    <>
      <section className="hero">
        <img
          className="hero__media"
          src={HERO_IMAGE}
          srcSet={`${HERO_IMAGE_SMALL} 900w, ${HERO_IMAGE} 1600w`}
          sizes="(max-width: 900px) 100vw, 1140px"
          alt="Dahlias and marigolds flowering in front of a greenhouse"
          loading="eager"
        />
        <div className="hero__scrim" aria-hidden="true" />
        <div className="hero__body">
          <p className="eyebrow eyebrow--light">South of England</p>
          <h1 className="hero__title">Everything that grows here</h1>
          <p className="hero__lede">
            Fruit trees, over thirty-five roses, dahlias and a vegetable patch -
            with the notes, timings and photographs from growing them.
          </p>
          <div className="hero__actions">
            <a href="#plants" className="btn btn-primary">Browse the plants</a>
            <Link to="/guides" className="btn btn-ghost">Growing guides</Link>
          </div>
        </div>
        <p className="hero__credit">Dahlias and marigolds in bloom</p>
      </section>

      <div className="hero-stats">
        <a href="#plants" className="hero-stat t-green">
          <span className="hero-stat__value">{total === null ? '-' : total}</span>
          <span className="hero-stat__label">Plants recorded</span>
        </a>
        <Link to="/guides" className="hero-stat t-plum">
          <span className="hero-stat__value">{guides.length}</span>
          <span className="hero-stat__label">Growing guides</span>
        </Link>
        <a href="#this-month" className="hero-stat t-amber">
          <span className="hero-stat__value">12</span>
          <span className="hero-stat__label">Monthly task lists</span>
        </a>
        <a href="#forecast" className="hero-stat t-teal">
          <span className="hero-stat__value">Apr&ndash;Oct</span>
          <span className="hero-stat__label">Frost-free window</span>
        </a>
      </div>

      <div className="layout-split">
        <MonthPanel />
        <WeatherWidget />
      </div>

      <GuideNotes />

      <PhotoGallery />

      <section className="section" id="plants" aria-labelledby="plants-heading">
        <div className="section-head">
          <div>
            <p className="eyebrow">Collection</p>
            <h2 className="section-title" id="plants-heading">The plants</h2>
          </div>
          <p className="section-note">
            {loading ? 'Loading...' : `${plants.length} ${plants.length === 1 ? 'record' : 'records'}${filtered ? ' matching' : ''}`}
          </p>
        </div>

        <div className="filter-bar">
          <div className="chips" role="group" aria-label="Filter by category">
            {FILTERS.map(c => (
              <button
                type="button"
                key={c.value || 'all'}
                className={category === c.value ? 'chip is-active' : 'chip'}
                onClick={() => setCategory(c.value)}
                aria-pressed={category === c.value}
              >
                {c.label}
              </button>
            ))}
          </div>
          <SearchBar search={search} setSearch={setSearch} />
        </div>

        {loading ? (
          <p className="empty-state">Loading plants...</p>
        ) : plants.length === 0 ? (
          <p className="empty-state">
            {filtered
              ? 'No plants match those filters. Try a different category or search term.'
              : 'No plants recorded yet.'}
          </p>
        ) : (
          <div className="plants-grid">
            {plants.map(plant => <PlantCard key={plant.plantId} plant={plant} />)}
          </div>
        )}
      </section>
    </>
  );
}
